/**
 * Stage 40 — the API shape of «показать реальные перевозки за этим числом» (scenarios 6–7).
 *
 * WHAT THE ENDPOINT RETURNS
 * Up to 50 rows of the frozen leg corpus that the window count was computed over, plus the
 * EXACT size of the full set. The list is a sample for checking; the number stays whole.
 *
 * THE IDENTITY GATE LIVES ON THE SERVER
 * `status` is `ok` only when the server re-counted the enumerated rows and got exactly the
 * value the model was given. Otherwise it is `examples_unavailable` with a reason, and the
 * UI says so instead of showing rows that might belong to a different set.
 *
 * This module has no formatting logic beyond turning one row into the line an analyst
 * reads; it never computes a count and never re-selects rows.
 */
import { decodeVariantGroupKey } from '../anomaly-semantics'
import type { Formatter } from '@/lib/i18n'
import { formatWeightDifference } from '../weight-format'

export interface EvidenceExampleItem {
  /**
   * Точная идентичность записи корпуса — «год|файл|лист|номер строки».
   *
   * Stage 43. Единственный ключ, по которому у строки можно спросить подробности: номер
   * отправки не уникален (одна отправка едет на многих вагонах), дата и вагон повторяются,
   * а `rowKey` — ключ React, а не идентификатор источника. Наружу как подпись не
   * показывается: он опознаёт запись корпуса, а не кейс.
   */
  sourceUid?: string | null
  /**
   * Значение ключа, по которому строка попала в набор, и что оно значит на её стороне.
   *
   * У всех строк набора значение ОДНО — это ключ запроса; различается роль. Колонка нужна
   * именно поэтому: она показывает, что импортная строка принадлежит тому же участнику, в
   * роли получателя, а не что показаны чужие строки.
   */
  keyValue?: string | null
  keyRole?: 'sender' | 'receiver' | 'payer' | string | null
  /**
   * Stable identity of the record, when the server supplies one.
   *
   * Two different movements can carry the same shipment numbers on the same day — one
   * consignment loaded onto two wagons is ordinary — so a key built from the visible
   * fields collides and React would drop one of them.
   */
  rowKey?: string | null
  /** ISO date of the corpus event — the date the window was measured against. */
  date: string
  /**
   * Which record this row is.
   *
   * Scenarios 6–7 count legs, so a row is one leg (`import` / `export`). Scenarios 1–5
   * count pairs, so a row is a whole movement (`pair`) and carries both shipment numbers
   * and the interval between them. Same list, same panel — only the head line differs.
   */
  side: 'import' | 'export' | 'pair' | string
  shipmentId: string | null
  wagon: string | null
  container: string | null
  cargo: string | null
  cargoEtsng: string | null
  cargoGng: string | null
  stationFrom: string | null
  stationTo: string | null
  countryFrom: string | null
  countryTo: string | null
  weightTons: number | null
  /* ── pair-shaped rows (scenarios 1–5) ─────────────────────────────────────── */
  importDate?: string | null
  daysBetween?: number | null
  importShipmentId?: string | null
  exportShipmentId?: string | null
  importWeightTons?: number | null
  /**
   * Обе ноги пары по отдельности.
   *
   * У пары «импорт → экспорт» два груза, два кода каждого классификатора, два
   * плательщика и две массы. Поля выше (`cargo`, `cargoEtsng`, `cargoGng`,
   * `weightTons`) несут ОДНО схлопнутое значение — экспортную сторону с запасным
   * значением импортной, — и как характеристика всей перевозки читаются неверно.
   * Эти поля приходят из тех же колонок той же строки корпуса и не подставляются
   * одно вместо другого: пусто на одной ноге остаётся пустым.
   *
   * Одноногие строки сценариев 6–7 их не несут — там строка и есть одна нога.
   */
  importCargoName?: string | null
  exportCargoName?: string | null
  importEtsng?: string | null
  exportEtsng?: string | null
  importGng?: string | null
  exportGng?: string | null
  importPayerCode?: string | null
  exportPayerCode?: string | null
  exportWeightTons?: number | null
  /**
   * Физическая идентичность обеих ног — «файл выгрузки|номер строки в нём».
   *
   * Единственный ключ, по которому подробности строки можно запросить точно.
   * `rowKey` (он же `pair_id`) — md5 от пары unit_record_id и на другом refresh
   * может смениться; номер отправки не уникален (одна отправка едет на нескольких
   * вагонах); отображаемый case_id к исторической записи не относится вовсе.
   * Наружу как идентификатор не показывается — только как ключ запроса.
   */
  importSourceRecordId?: string | null
  exportSourceRecordId?: string | null
}

/**
 * One value of a «сколько ещё вариантов» number, with a few movements from inside it.
 *
 * The count is the whole group; `examples` is a short sample capped at
 * `MAX_VARIANT_EXAMPLES` — a list of variants would be unreadable if every variant
 * unrolled its full history.
 */
export interface EvidenceVariantItem {
  groupKey: string
  count: number
  examplesLimit: number
  examples: EvidenceExampleItem[]
}

export function isVariantItem(item: unknown): item is EvidenceVariantItem {
  return !!item && typeof item === 'object' && Array.isArray((item as EvidenceVariantItem).examples)
}

/**
 * Чем набор ключуется и что этот ключ значит на каждой стороне корпуса (Stage 43).
 *
 * Приходит с сервера целиком. Фронт его НЕ выводит из названия признака: `exp_participant`
 * пробуется экспортной ногой, но считает ноги обеих сторон, и вывод «раз exp — значит
 * экспорт» и был исходной ошибкой.
 */
export interface EvidenceSubject {
  family: string
  keyColumn: string
  keyValue: string | null
  keyKind: string | null
  keyLabel: string | null
  roleBySide: Record<string, string> | null
  /** Нога карточки, из которой прочитан ключ. */
  probeSide: string | null
  probeField: string | null
  /** `both` — в счёт входят ноги обеих сторон. */
  sideScope: string | null
  windowDays: number | null
  windowStart: string | null
  windowEnd: string | null
  /** Название организации — только когда нога карточки несёт ТОТ ЖЕ ключ. */
  name: string | null
  nameSource: string | null
}

export interface EvidenceExamplesData {
  evidenceKey: string
  datasetType: string | null
  feature: string
  title: string | null
  status: 'ok' | 'examples_unavailable' | string
  reason: string | null
  /** Stage 43 — кто/что стоит за числом. */
  subject?: EvidenceSubject | null
  /** Точный состав набора по сторонам. Сумма равна `totalCount` по построению. */
  sideCounts?: { import: number; export: number } | null
  /** Size of the FULL set, never the number of rows returned. */
  totalCount: number | null
  returnedCount: number
  limit: number
  truncated: boolean
  selectionDescription: string | null
  items: (EvidenceExampleItem | EvidenceVariantItem)[]
}

/**
 * Подробности ОДНОЙ строки примеров (Stage 43) — то, чего в десяти колонках нет.
 *
 * Аналог карточки «Детали исторической перевозки» сценариев 1–5, но у 6–7 строка — ОДНА
 * нога, а не пара: две ноги ей взять неоткуда, и придумывать вторую нельзя.
 */
export interface EvidenceRowLeg {
  shipmentId: string | null
  wagonNumber: string | null
  containerNumber: string | null
  unitType: 'wagon' | 'container' | string | null
  cargoName: string | null
  cargoEtsng: string | null
  cargoGng: string | null
  weightTons: number | null
  countryFrom: string | null
  countryTo: string | null
  stationFrom: string | null
  stationTo: string | null
  stationOp: string | null
  stationDis: string | null
  borderIn: string | null
  borderOut: string | null
  senderName: string | null
  senderBin: string | null
  receiverName: string | null
  receiverBin: string | null
  payerName: string | null
  payerCode: string | null
  operationAt: string | null
  departureAt: string | null
  arrivalAt: string | null
  acceptAt: string | null
  deliveryAt: string | null
  creditAt: string | null
  routeText: string | null
}

/**
 * Есть ли у этой записи корпуса кейс — и ровно один ли.
 *
 * `exact` — ровно одна кандидатная пара называет эту ногу своей; только тогда кейс можно
 * открыть. `ambiguous` — пар несколько, и выбрать одну значило бы выдать догадку за факт.
 * `none` — ногу не взяла ни одна пара: это просто событие корпуса, кейса у него нет.
 */
export interface EvidenceRowCase {
  status: 'exact' | 'ambiguous' | 'none' | string
  matchedOn: string | null
  candidateCount: number
  canonicalPairId: string | null
  scenarioId: number | null
  stableCaseGroupId: string | null
  legRole: 'import' | 'export' | string | null
  /** `null` — витрина не отвечала на вопрос; читать как «исключён» нельзя. */
  userSurfaceEligible: boolean | null
}

export interface EvidenceRowDetailData {
  sourceUid: string
  side: string | null
  eventDate: string | null
  detailStatus: 'ok' | 'corpus_row_unavailable' | string
  leg: EvidenceRowLeg | null
  case: EvidenceRowCase
}

/** «05.09.2025» — the corpus event date, in the format the rest of the card uses. */
export function formatExampleDate(iso: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ''))
  return m ? `${m[3]}.${m[2]}.${m[1]}` : null
}

export interface RenderedExample {
  /** Stable key for React — the corpus row is identified by side + shipment + date. */
  key: string
  head: string
  detail: string
}

const SIDE_LABEL: Record<string, string> = { import: 'Импорт', export: 'Экспорт', pair: 'Перевозка' }

/* ── Stage 41: the same record, split into cells instead of joined into a line ──
 *
 * The two shapes are built from ONE extraction (`toExampleCells`), so the table and the
 * legacy single-line form can never disagree about what a row says. Nothing here selects,
 * counts or re-orders: it renames server fields into the analyst's vocabulary and formats
 * the values that already have a formatter.
 *
 * Every field is nullable on purpose. A missing value is `null` and the table prints «—»
 * or drops the line — it is never «ЕТСНГ undefined».
 */
export interface ExampleCells {
  /** Stable React key — the server's row identity when it supplies one. */
  key: string
  /** Which record this is: a whole movement (`pair`) or one leg (`import` / `export`). */
  side: string
  sideLabel: string | null
  /** «18.12.2025» — the corpus event date the window was measured against. */
  date: string | null
  /** «27.11.2025» — the import leg of a pair row, when the server carries it. */
  importDate: string | null
  /** Pair rows name both legs; leg rows name their own shipment. */
  importShipment: string | null
  exportShipment: string | null
  shipment: string | null
  /** The physical unit and what kind of unit it is — the table labels them differently. */
  unit: string | null
  unitKind: 'wagon' | 'container' | null
  cargo: string | null
  etsng: string | null
  gng: string | null
  /** «КИТАЙ → РОССИЯ» */
  route: string | null
  /** «Достык (эксп.) → Куйбас» */
  stations: string | null
  /** «69.000 т», through the shared weight formatter. */
  weight: string | null
  importWeight: string | null
  /** Interval of a pair row, as a number — the column header carries the unit. */
  days: number | null
  /* ── обе ноги пары ────────────────────────────────────────────────────────
   *
   * Заполнены ТОЛЬКО у строк-пар (`side === 'pair'`) и только из полей своей
   * ноги. Ни одно значение не подставляется с другой стороны: если импортного
   * груза в строке нет, здесь `null`, и таблица печатает «—», а не название
   * экспортного груза. У одноногих строк 6–7 все они `null` — такая строка и
   * есть одна нога, делить ей нечего.
   */
  importCargo: string | null
  exportCargo: string | null
  importEtsng: string | null
  exportEtsng: string | null
  importGng: string | null
  exportGng: string | null
  /** Код плательщика ноги. Названия плательщика канонический источник не несёт. */
  importPayer: string | null
  exportPayer: string | null
  /** «66.460 т» — масса именно экспортной ноги, без запасного значения импортной. */
  exportWeight: string | null
  /**
   * Точная идентичность обеих ног — ключ подробной карточки строки.
   *
   * Есть только у строк-пар и только когда сервис её прислал. Пусто → строка не
   * кликабельна: открывать подробности «примерно этой» перевозки нельзя.
   */
  importRecordId: string | null
  exportRecordId: string | null
  /**
   * Идентичность одноногой строки 6–7 — её `source_uid`. Пусто → строка не кликабельна.
   */
  sourceUid: string | null
  /** «БИН 970340000843» / «код 2759871» — ключ, по которому строка попала в набор. */
  participant: string | null
  /** «получатель» / «отправитель» / «плательщик» — роль на стороне ЭТОЙ строки. */
  participantRole: string | null
}

/** Обе ноги опознаны — по строке можно открыть подробную карточку. */
export function hasExactIdentity(r: ExampleCells): boolean {
  return r.side === 'pair' && !!r.importRecordId && !!r.exportRecordId
}

/** Одноногая строка 6–7 опознана точно — можно открыть подробности записи корпуса. */
export function hasExactLegIdentity(r: ExampleCells): boolean {
  return r.side !== 'pair' && !!r.sourceUid
}

const KEY_ROLE_TEXT: Record<string, string> = {
  sender: 'отправитель',
  receiver: 'получатель',
  payer: 'плательщик',
}

/** «БИН 970340000843» — подпись зависит от того, ЧТО за ключ, а не от догадки по виду. */
function keyValueText(value: string | null | undefined, kind: string | null | undefined): string | null {
  const v = value === null || value === undefined ? null : String(value).trim() || null
  if (!v) return null
  return kind === 'payer_code' ? `код ${v}` : `БИН ${v}`
}

/** Field extraction for one corpus row. No business logic, no re-selection. */
export function toExampleCells(
  item: EvidenceExampleItem,
  index: number,
  /** Чем ключуется набор — нужно только чтобы подписать ключ строки, не чтобы отобрать. */
  subject?: EvidenceSubject | null,
): ExampleCells {
  const weightText = (v: number | null | undefined): string | null =>
    typeof v === 'number' && Number.isFinite(v)
      ? formatWeightDifference({ importWeight: v, exportWeight: v, weightDiffPct: null }).importWeightText
      : null
  // Поля ноги берутся только у строк-пар. Одноногая строка 6–7 несёт одно
  // значение, и разложить его на «импорт» и «экспорт» нельзя — там `null`.
  const leg = <T,>(v: T | null | undefined): T | null =>
    item.side === 'pair' ? (v ?? null) : null
  return {
    key: item.rowKey ?? `${item.side}_${item.shipmentId ?? item.exportShipmentId ?? 'na'}_${item.date}_${index}`,
    side: item.side,
    sideLabel: SIDE_LABEL[item.side] ?? null,
    date: formatExampleDate(item.date),
    importDate: item.side === 'pair' ? formatExampleDate(item.importDate) : null,
    importShipment: item.side === 'pair' ? (item.importShipmentId ?? null) : null,
    exportShipment: item.side === 'pair' ? (item.exportShipmentId ?? null) : null,
    shipment: item.side === 'pair' ? null : (item.shipmentId ?? null),
    unit: item.wagon ?? item.container ?? null,
    unitKind: item.wagon ? 'wagon' : item.container ? 'container' : null,
    cargo: item.cargo ?? null,
    etsng: item.cargoEtsng ?? null,
    gng: item.cargoGng ?? null,
    route: item.countryFrom && item.countryTo ? `${item.countryFrom} → ${item.countryTo}` : null,
    stations: item.stationFrom && item.stationTo ? `${item.stationFrom} → ${item.stationTo}` : null,
    weight: weightText(item.weightTons),
    importWeight: item.side === 'pair' ? weightText(item.importWeightTons) : null,
    days: typeof item.daysBetween === 'number' && Number.isFinite(item.daysBetween)
      ? Math.abs(Math.trunc(item.daysBetween))
      : null,
    importCargo: leg(item.importCargoName),
    exportCargo: leg(item.exportCargoName),
    importEtsng: leg(item.importEtsng),
    exportEtsng: leg(item.exportEtsng),
    importGng: leg(item.importGng),
    exportGng: leg(item.exportGng),
    importPayer: leg(item.importPayerCode),
    exportPayer: leg(item.exportPayerCode),
    exportWeight: item.side === 'pair' ? weightText(item.exportWeightTons) : null,
    importRecordId: leg(item.importSourceRecordId),
    exportRecordId: leg(item.exportSourceRecordId),
    sourceUid: item.side === 'pair' ? null : (item.sourceUid ?? null),
    participant: keyValueText(item.keyValue, subject?.keyKind ?? null),
    participantRole: item.keyRole ? (KEY_ROLE_TEXT[String(item.keyRole)] ?? null) : null,
  }
}

/** Every row the server returned, in the server's order. The UI never re-sorts. */
export function exampleCells(data: EvidenceExamplesData | undefined): ExampleCells[] {
  return (data?.items ?? [])
    .filter((i): i is EvidenceExampleItem => !isVariantItem(i))
    .map((item, i) => toExampleCells(item, i, data?.subject ?? null))
}

/**
 * «45 030 импортных · 47 149 экспортных» — состав набора по сторонам.
 *
 * Печатается над таблицей, а не выводится из заголовка причины: причина названа по стороне
 * ПРОБЫ (экспортная нога кейса), а набор — обе стороны корпуса, и пока это не сказано,
 * импортная строка читается как ошибка отбора. `null` — сервер состав не прислал.
 */
export function sideCountsText(data: EvidenceExamplesData | undefined): string | null {
  const c = data?.sideCounts
  if (!c) return null
  const n = (v: number) => v.toLocaleString('ru-RU')
  const parts = [
    c.import > 0 ? `${n(c.import)} импортных` : null,
    c.export > 0 ? `${n(c.export)} экспортных` : null,
  ].filter(Boolean)
  return parts.length ? `Из них ${parts.join(' · ')}` : null
}

/** «ТОО «Богатырь Комир» · БИН 970340000843» — субъект набора, названный один раз. */
export function subjectText(data: EvidenceExamplesData | undefined): string | null {
  const s = data?.subject
  if (!s) return null
  const key = keyValueText(s.keyValue, s.keyKind)
  if (!key) return null
  return s.name ? `${s.name} · ${key}` : key
}

/**
 * One «сколько ещё вариантов» group, ready for the table: its own full count and the
 * capped sample of movements inside it, plus the cargo identity the group is named by.
 */
export interface VariantGroup {
  key: string
  /** The group's own count — the whole variant, not the sample below it. */
  count: number
  /** Cargo of the variant, taken from its first example (the server groups by it). */
  cargo: string | null
  etsng: string | null
  gng: string | null
  /** Server's grouping key, shown only when there is no cargo to name the group by. */
  groupKey: string
  /**
   * Ключ группы словами — «ГНГ 44101130 → ГНГ 99220000».
   *
   * Это единственное, что различает варианты между собой: у одного вагона пять групп
   * подряд могут иметь одинаковое название груза (например «Вагоны железнодорожные,
   * перевозимые на своих осях») и различаться только кодом второй ноги. Заголовок,
   * построенный по названию груза из первого примера, в таком случае повторяется, и
   * счётчик вариантов выглядит ошибкой. Значение берётся из `groupKey` сервера и не
   * меняет ни состав групп, ни их число.
   */
  keyLabel: string | null
  rows: ExampleCells[]
}

export function variantGroups(data: EvidenceExamplesData | undefined): VariantGroup[] {
  return (data?.items ?? []).filter(isVariantItem).map((v, i) => {
    const first = v.examples[0]
    return {
      key: `${v.groupKey}_${i}`,
      count: v.count,
      cargo: first?.cargo ?? null,
      etsng: first?.cargoEtsng ?? null,
      gng: first?.cargoGng ?? null,
      groupKey: v.groupKey,
      keyLabel: decodeVariantGroupKey(v.groupKey),
      rows: v.examples.map((it, j) => toExampleCells(it, j, data?.subject ?? null)),
    }
  })
}

/** «3 дня» — the interval a pair row is built on, in the analyst's words. */
function daysPhrase(n: number): string {
  const a = Math.abs(Math.trunc(n)), m10 = a % 10, m100 = a % 100
  const word = m10 === 1 && m100 !== 11 ? 'день'
    : m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14) ? 'дня'
      : 'дней'
  return `${a} ${word}`
}

/**
 * One corpus row as two readable lines.
 *
 * Source uids, file names, sheets and row numbers are deliberately absent: they identify
 * the record for a pipeline, not for an analyst, and the card has never shown them.
 */
export function renderExample(item: EvidenceExampleItem, index: number): RenderedExample {
  const c = toExampleCells(item, index)
  const unit = c.unit ? `${c.unitKind === 'container' ? 'контейнер' : 'вагон'} ${c.unit}` : null
  // A pair row names both legs; a leg row names its own shipment.
  const shipments = c.side === 'pair'
    ? [c.importShipment ? `импорт ${c.importShipment}` : null,
       c.exportShipment ? `экспорт ${c.exportShipment}` : null].filter(Boolean).join(' → ') || null
    : c.shipment ? `отправка ${c.shipment}` : null
  const head = [c.date, c.sideLabel, shipments, unit].filter(Boolean).join(' · ')
  const detail = [c.cargo, c.etsng ? `ЕТСНГ ${c.etsng}` : null, c.route, c.stations, c.weight,
    c.days != null ? daysPhrase(c.days) : null].filter(Boolean).join(' · ')
  return { key: c.key, head: head || '—', detail }
}

/** Every row the server returned, in the server's order. The UI never re-sorts. */
export function renderExamples(data: EvidenceExamplesData | undefined): RenderedExample[] {
  return (data?.items ?? []).filter((i): i is EvidenceExampleItem => !isVariantItem(i)).map(renderExample)
}

/** One variant: its own count, then the movements sampled from inside it. */
export interface RenderedVariant {
  key: string
  head: string
  rows: RenderedExample[]
}

/**
 * Variants keep their own counts.
 *
 * The head line states how many movements the variant holds in full; the nested rows are
 * a capped sample of them, so a short list can never read as the whole variant.
 */
export function renderVariants(data: EvidenceExamplesData | undefined, f: Formatter): RenderedVariant[] {
  return (data?.items ?? []).filter(isVariantItem).map((v, i) => {
    const first = v.examples[0]
    const label = [first?.cargo, first?.cargoEtsng ? `ЕТСНГ ${first.cargoEtsng}` : null]
      .filter(Boolean).join(' · ')
    return {
      key: `${v.groupKey}_${i}`,
      head: `${label || v.groupKey} — ${f.number(v.count)}`,
      rows: v.examples.map(renderExample),
    }
  })
}
